
import { Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReactConfetti from 'react-confetti';
import { useState, useEffect } from 'react';
import { useQueryClient } from "@tanstack/react-query";

interface PrizeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prize: { name: string; description: string } | null;
  onClaim: () => void;
}

export function PrizeDialog({ open, onOpenChange, prize, onClaim }: PrizeDialogProps) {
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [showConfetti, setShowConfetti] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleResize = () =>  setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleClose = async () => {
    onOpenChange(false);
    onClaim();
    await queryClient.invalidateQueries({ queryKey: ['approved-tips'] });
  };

  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {showConfetti && (
        <ReactConfetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={20000}
          gravity={0.3}
          colors={['#00A3E0', '#FF4D8C', '#002B49', '#FFFFFF']}
        />
      )}
      <DialogContent className="sm:max-w-md animate-scale-in">
        <DialogHeader>
          <DialogTitle className="text-center animate-fade-in">Congratulations! You've Won!</DialogTitle>
        </DialogHeader>
        <div className="relative overflow-hidden rounded-lg p-6 bg-gradient-to-r from-vaseline-blue/10 to-vaseline-pink/10">
          <div className="absolute inset-0 bg-white/40 backdrop-blur-sm animate-fade-in" />
          <div className="relative z-10 text-center">
            <Sparkles className="w-12 h-12 text-yellow-500 mx-auto mb-4 animate-bounce" />
            <h3 className="text-xl font-bold text-vaseline-navy mb-2 animate-fade-in">{prize?.name}</h3>
            <p className="text-gray-600 mb-4 animate-fade-in">{prize?.description}</p>
            <button 
              onClick={handleClose}
              className="px-6 py-2 bg-vaseline-blue text-white rounded-full font-medium hover:bg-vaseline-blue/90 transition-all duration-300 hover:scale-105 animate-fade-in"
            >
              Show Staff to Claim Prize
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

